import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { AIWorker, WorkerStatus } from '../types';
import { Worker3D } from './Worker3D';

const API = (process.env.REACT_APP_API_URL || 'http://localhost:3001').replace(/\/+$/, '');

const statusColors: Record<WorkerStatus, string> = {
  working: '#22c55e',
  idle: '#94a3b8',
  done: '#3b82f6',
  error: '#ef4444',
  waiting: '#f59e0b',
};

const statusLabels: Record<WorkerStatus, string> = {
  working: 'Работает',
  idle: 'Свободен',
  done: 'Готово',
  error: 'Ошибка',
  waiting: 'Ожидает',
};

interface Props {
  worker: AIWorker;
  onClick: (worker: AIWorker) => void;
  isSelected: boolean;
}

export const WorkerAvatar: React.FC<Props> = ({ worker, onClick, isSelected }) => {
  const isActive = worker.status === 'working';
  const [stopping, setStopping] = useState(false);

  // Stop = SIGTERM the active claude process and flip the task row.
  // Same endpoint WorkerDetail uses; surfacing it on the card means
  // the operator can kill a runaway $$$ task without first clicking
  // into the side panel. e.stopPropagation so the parent onClick
  // (which opens WorkerDetail) doesn't fire.
  const stopCurrent = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!worker.currentTask) return;
    const cost = worker.currentTask.costUsd
      ? `$${worker.currentTask.costUsd.toFixed(2)}`
      : '?';
    if (!window.confirm(`Остановить ${worker.name}? Текущая трата ${cost} зафиксируется в Истории.`)) return;
    setStopping(true);
    try {
      const r = await fetch(`${API}/api/tasks/${worker.currentTask.id}/cancel`, { method: 'POST' });
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        alert(`Не удалось остановить: ${data.message || data.error || `HTTP ${r.status}`}`);
      }
    } catch (err: any) {
      alert(`Сетевая ошибка: ${err.message || 'unknown'}`);
    } finally {
      setStopping(false);
    }
  };

  return (
    <motion.div
      className="worker-card"
      onClick={() => onClick(worker)}
      whileHover={{ scale: 1.03, y: -4 }}
      whileTap={{ scale: 0.98 }}
      animate={isActive ? { boxShadow: ['0 0 0px #22c55e40', '0 0 20px #22c55e40', '0 0 0px #22c55e40'] } : {}}
      transition={isActive ? { duration: 2, repeat: Infinity } : { duration: 0.2 }}
      style={{
        border: isSelected ? '2px solid #8b5cf6' : '2px solid transparent',
        background: isSelected ? 'rgba(139, 92, 246, 0.08)' : undefined,
      }}
    >
      {/* 3D Avatar */}
      <div className="worker-3d-container">
        <Worker3D workerId={worker.id} status={worker.status} />
        <motion.div
          className="worker-status-dot"
          style={{ background: statusColors[worker.status] }}
          animate={isActive ? { scale: [1, 1.3, 1] } : {}}
          transition={isActive ? { duration: 1, repeat: Infinity } : {}}
        />
      </div>

      {/* Info */}
      <div className="worker-info">
        <h3 className="worker-name">{worker.name}</h3>
        <div className="worker-role-row">
          <span className="worker-role">{worker.role.toUpperCase()}</span>
          <span className={`worker-engine-badge ${worker.role === 'orchestrator' ? 'badge-claw' : 'badge-claude'}`}>
            {worker.role === 'orchestrator' ? 'CW' : 'CC'}
          </span>
        </div>
        <span className="worker-status-label" style={{ color: statusColors[worker.status] }}>
          {statusLabels[worker.status]}
        </span>
      </div>

      {/* Progress bar */}
      {worker.currentTask && (
        <div className="worker-progress-container">
          <div className="worker-progress-label">{worker.currentTask.module}</div>
          <div className="worker-progress-bar">
            <motion.div
              className="worker-progress-fill"
              initial={{ width: 0 }}
              animate={{ width: `${worker.currentTask.progress}%` }}
              transition={{ duration: 1, ease: 'easeOut' }}
              style={{ background: `linear-gradient(90deg, ${statusColors[worker.status]}, ${statusColors[worker.status]}aa)` }}
            />
          </div>
          <div className="worker-progress-row">
            <span className="worker-progress-text">{worker.currentTask.progress}%</span>
            {/* Live cost surfaces here from 'task:tokens' WS events
                — same source as the WorkerDetail meter, but visible
                without opening the side panel so you don't have to
                drill in to see what's burning. */}
            {worker.currentTask.costUsd !== undefined && worker.currentTask.costUsd > 0 && (
              <span className="worker-cost-pill" title={`In: ${(worker.currentTask.inputTokens || 0).toLocaleString('ru-RU')} · Out: ${(worker.currentTask.outputTokens || 0).toLocaleString('ru-RU')} · ${worker.currentTask.model || ''}`}>
                {worker.currentTask.costUsd >= 1
                  ? `$${worker.currentTask.costUsd.toFixed(2)}`
                  : `$${worker.currentTask.costUsd.toFixed(4).replace(/0+$/, '').replace(/\.$/, '.0')}`}
              </span>
            )}
            {isActive && (
              <button
                type="button"
                className="worker-stop-btn"
                onClick={stopCurrent}
                disabled={stopping}
                title="Остановить задачу — SIGTERM Claude, спенд зафиксируется"
                aria-label="Остановить текущую задачу"
              >
                {stopping ? '…' : '◼ STOP'}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="worker-stats">
        <span>{worker.completedTasks}/{worker.totalTasks || '∞'} задач</span>
        <span>{worker.lastActivity}</span>
      </div>
    </motion.div>
  );
};
